import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { publisherService } from "../services/publisherService";
import {
  PublisherValuesInit,
  getPublisherCreateVariables,
  getPublisherValues,
  PublisherValuesType,
  PublisherErrorsType,
  publisherDisplayId,
} from "./PublisherCommonStore";
import { Publisher, SortOrder } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getCountryValues,
  CountryValuesType,
} from "../../country/store/CountryCommonStore";
import {
  countryDisplayIdByDemonym,
  countryDisplayId,
} from "../../country/store/CountryCommonStore";
import { validateMask } from "../../common/utils";
import { normalizeAndValidateEmail } from "../../../common/utils";
import {
  getProvinceStateValues,
  ProvinceStateValuesType,
} from "../../provinceState/store/ProvinceStateCommonStore";
import { GridColumn } from "../../common/types";
import { PublisherRepresentativeCreateStore } from "../../publisherRepresentative/store";
import { PublisherCollectingSocietyCreateStore } from "../../publisherCollectingSociety/store";
import { GridChangeSet } from "../../common/types";
import { PersonType } from "../../../api/graphql";
import { MaritalStatus } from "../../../api/graphql";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { countryService } from "../../country/services/countryService";
import { provinceStateService } from "../../provinceState/services/provinceStateService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class PublisherCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: PublisherErrorsType = {};
  service = publisherService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    basicInformation: { open: true, errors: [] },
    address: { open: true, errors: [] },
    legalInformation: { open: true, errors: [] },
    systemDates: { open: false, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: Publisher | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = PublisherValuesInit();
  personTypeError: string | undefined;
  nameError: string | undefined;
  maritalStatusError: string | undefined;
  nationalityError: string | undefined;
  nationalityService = countryService;
  nationalitySearchValue: string = "";
  nationalityOptions: CountryValuesType[] = [];
  pseudonymError: string | undefined;
  taxIdError: string | undefined;
  phoneError: string | undefined;
  emailError: string | undefined;
  streetAndNumberError: string | undefined;
  floorError: string | undefined;
  suiteError: string | undefined;
  postalCodeError: string | undefined;
  countryError: string | undefined;
  countryService = countryService;
  countrySearchValue: string = "";
  countryOptions: CountryValuesType[] = [];
  provinceError: string | undefined;
  provinceService = provinceStateService;
  provinceSearchValue: string = "";
  provinceOptions: ProvinceStateValuesType[] = [];
  cityError: string | undefined;
  caeIpiError: string | undefined;
  representativesError: string | undefined;
  representativesVisibleColumns: GridColumn[];
  representativesCreateStore: PublisherRepresentativeCreateStore;
  managementSocietiesError: string | undefined;
  managementSocietiesVisibleColumns: GridColumn[];
  managementSocietiesCreateStore: PublisherCollectingSocietyCreateStore;
  createdAt_Error: string | undefined;
  lastUpdatedAt_Error: string | undefined;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<PublisherValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.representativesVisibleColumns = [
      { name: "name", title: this.langStore.PublisherRepresentativeNameLabel },
      { name: "role", title: this.langStore.PublisherRepresentativeRoleLabel },
    ];
    this.representativesCreateStore = new PublisherRepresentativeCreateStore(
      rootStore,
      "representatives_" + this.storeId
    );
    this.managementSocietiesVisibleColumns = [
      {
        name: "__collectingSociety",
        title: this.langStore.PublisherCollectingSocietyCollectingSocietyLabel,
      },
      {
        name: "cmsAccountNumber",
        title: this.langStore.PublisherCollectingSocietyCmsAccountNumberLabel,
      },
    ];
    this.managementSocietiesCreateStore =
      new PublisherCollectingSocietyCreateStore(
        rootStore,
        "managementSocieties_" + this.storeId
      );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<PublisherValuesType>);
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<PublisherValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return publisherDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PublisherSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity
      if (params.callerData?.entityName === "PublisherRepresentative") {
        this.representativesCreateStore.isWaitingForCallback = false;
        this.representativesCreateStore.callbackReturnPath = null;
        yield this.representativesCreateStore.returnedData(params);
        return;
      }
      if (params.callerData?.entityName === "PublisherCollectingSociety") {
        this.managementSocietiesCreateStore.isWaitingForCallback = false;
        this.managementSocietiesCreateStore.callbackReturnPath = null;
        yield this.managementSocietiesCreateStore.returnedData(params);
        return;
      }

      if (params.callerData?.fieldName === "nationality") {
        if (params.returnData.entity) {
          const displayId =
            countryDisplayIdByDemonym(
              params.returnData.entity,
              this.langStore
            ) || String(params.returnData.entity.id);
          this.entityValues.showNationality = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.countryOne({
            id: params.returnData.resultId,
          });
          const country = result.countryOne;
          this.entityValues.showNationality = {
            displayId: countryDisplayIdByDemonym(country, this.langStore),
            id: country.id,
          };
          this.setNationalityOptions([
            {
              ...getCountryValues(country, this.langStore),
              displayId: countryDisplayIdByDemonym(country, this.langStore),
            },
          ]);
        }
      } else if (params.callerData?.fieldName === "country") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showCountry = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.countryOne({
            id: params.returnData.resultId,
          });
          const country = result.countryOne;
          this.entityValues.showCountry = {
            displayId: country.displayId,
            id: country.id,
          };
          this.setCountryOptions([getCountryValues(country, this.langStore)]);
        }
      } else if (params.callerData?.fieldName === "province") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showProvince = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.provinceStateOne({
            id: params.returnData.resultId,
          });
          const provinceState = result.provinceStateOne;
          this.entityValues.showProvince = {
            displayId: provinceState.displayId,
            id: provinceState.id,
          };
          this.setProvinceOptions([
            getProvinceStateValues(provinceState, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "basicInformation",
        label: this.rootStore.langStore.groupPublisherBasicInformationLabel,
        errors: this.getCardErrors("basicInformation"),
      },
      {
        id: "address",
        label: this.rootStore.langStore.groupPublisherAddressLabel,
        errors: this.getCardErrors("address"),
      },
      {
        id: "legalInformation",
        label: this.rootStore.langStore.groupPublisherLegalInformationLabel,
        errors: this.getCardErrors("legalInformation"),
      },
      {
        id: "systemDates",
        label: this.rootStore.langStore.groupPublisherSystemDatesLabel,
        errors: this.getCardErrors("systemDates"),
      },
    ];
  }

  getPersonTypeValue(): PersonType {
    return this.entityValues.personType;
  }

  setPersonTypeValue(value: PersonType) {
    this.entityValues.personType = value;
  }

  getPersonTypeOptions() {
    return this.langStore.PersonTypeOptions;
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  getMaritalStatusValue(): MaritalStatus | "" {
    return this.entityValues.maritalStatus;
  }

  setMaritalStatusValue(value: MaritalStatus | "") {
    this.entityValues.maritalStatus = value;
  }

  getMaritalStatusOptions() {
    return this.langStore.MaritalStatusOptions;
  }

  setNationalityOptions(values: CountryValuesType[]) {
    this.nationalityOptions = values;
  }

  *loadNationalityValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Country");
    const allOptions = (cache.getOptions("Country") as CountryValuesType[]).map(
      (x: any) => ({
        ...x,
        displayId: countryDisplayIdByDemonym(x, this.langStore),
      })
    );

    const searchValue = this.nationalitySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setNationalityOptions(filtered);
    if (!this.entityValues.showNationality?.id) {
      const defaultOption = this.nationalityOptions.find(
        (x: any) => countryDisplayId(x, this.langStore) === "Argentina"
      );
      if (defaultOption) {
        this.entityValues.showNationality = {
          displayId: defaultOption.displayId,
          id: defaultOption.id,
        };
      }
    }
  }

  getNationalityOptions() {
    return this.nationalityOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setNationalityValue(value: string) {
    this.nationalitySearchValue = value;
    yield this.loadNationalityValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedNationality(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCountry(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "nationality"
      );
    } else {
      if (value) {
        this.entityValues.showNationality = value;
        this.entityValues.__nationality = value.displayId;
      }
    }
  }

  getPseudonymValue(): string {
    return this.entityValues.pseudonym;
  }

  setPseudonymValue(value: string) {
    this.entityValues.pseudonym = value;
  }

  getTaxIdValue(): string {
    return this.entityValues.taxId;
  }

  setTaxIdValue(value: string) {
    this.entityValues.taxId = value;
  }

  getPhoneValue(): string {
    return this.entityValues.phone;
  }

  setPhoneValue(value: string) {
    this.entityValues.phone = value;
  }

  getEmailValue(): string {
    return this.entityValues.email;
  }

  setEmailValue(value: string) {
    this.entityValues.email = value;
  }

  getStreetAndNumberValue(): string {
    return this.entityValues.streetAndNumber;
  }

  setStreetAndNumberValue(value: string) {
    this.entityValues.streetAndNumber = value;
  }

  getFloorValue(): string {
    return this.entityValues.floor;
  }

  setFloorValue(value: string) {
    this.entityValues.floor = value;
  }

  getSuiteValue(): string {
    return this.entityValues.suite;
  }

  setSuiteValue(value: string) {
    this.entityValues.suite = value;
  }

  getPostalCodeValue(): string {
    return this.entityValues.postalCode;
  }

  setPostalCodeValue(value: string) {
    this.entityValues.postalCode = value;
  }

  setCountryOptions(values: CountryValuesType[]) {
    this.countryOptions = values;
  }

  *loadCountryValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Country");
    const allOptions = cache.getOptions("Country") as CountryValuesType[];

    const searchValue = this.countrySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setCountryOptions(filtered);
  }

  getCountryOptions() {
    return this.countryOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setCountryValue(value: string) {
    this.countrySearchValue = value;
    yield this.loadCountryValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedCountry(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCountry(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "country"
      );
    } else {
      if (value) {
        this.entityValues.showCountry = value;
        this.entityValues.__country = value.displayId;
        yield this.loadProvinceValues();
        this.entityValues.showProvince = null;
        this.entityValues.__province = "";
      }
    }
  }

  setProvinceOptions(values: ProvinceStateValuesType[]) {
    this.provinceOptions = values;
  }

  *loadProvinceValues(): any {
    const parentId = this.entityValues.showCountry?.id;
    if (parentId) {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.provinceStateListWithOffset({
        offset: 0,
        take: 100,
        where: { country: { id: { equals: parentId } } },
        orderBy: [{ [this.langStore.fieldByLang("name")]: SortOrder.Asc }],
      });
      this.setProvinceOptions(
        (result.provinceStateListWithOffset || []).map((x: any) =>
          getProvinceStateValues(x, this.langStore)
        )
      );
    } else {
      this.setProvinceOptions([]);
    }
  }

  getProvinceOptions() {
    return this.provinceOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setProvinceValue(value: string) {
    this.provinceSearchValue = value;
    yield this.loadProvinceValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedProvince(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewProvinceState(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "province"
      );
    } else {
      if (value) {
        this.entityValues.showProvince = value;
        this.entityValues.__province = value.displayId;
      }
    }
  }

  getCityValue(): string {
    return this.entityValues.city;
  }

  setCityValue(value: string) {
    this.entityValues.city = value;
  }

  getCaeIpiValue(): string {
    return this.entityValues.caeIpi;
  }

  setCaeIpiValue(value: string) {
    this.entityValues.caeIpi = value;
  }

  representativesLoadRow(row: any) {
    this.representativesCreateStore.loadValuesFromRow(row);
  }

  representativesRestore(id: string) {
    const representatives = this.entityValues.showRepresentatives.filter(
      (x) => x.id === id
    )[0];
    if (representatives) {
      representatives.__status__ = restoredStatus(representatives.__status__);
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  representativesValidateChanges(_changes: GridChangeSet) {
    const errors = this.representativesCreateStore.validateData(
      this.representativesCreateStore.entityValues
    );

    // Uniqueness validation for subentity
    const currentValues = this.representativesCreateStore.entityValues;
    const existingRows = this.entityValues.showRepresentatives || [];
    const activeRows = existingRows.filter(
      (row: any) => !row.__status__?.includes("deleted")
    );

    // Uniqueness check: name, role
    const isDuplicate_name_role = activeRows.some(
      (row) =>
        row.id !== currentValues.id &&
        row.name === currentValues.name &&
        row.role === currentValues.role &&
        (currentValues.name || currentValues.role)
    );
    if (isDuplicate_name_role) {
      errors.name = {
        message: this.langStore.duplicateValueError,
        fieldLabel: this.langStore.PublisherRepresentativeNameLabel,
      };
    }

    this.representativesCreateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  representativesCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.representativesCreateStore.entityValues;
      const representatives = this.entityValues.showRepresentatives;
      for (
        let index = 0;
        index < this.entityValues.showRepresentatives.length;
        index++
      ) {
        if (representatives[index].id === values.id) {
          representatives[index] = {
            ...values,
            __status__: updatedStatus(values.__status__),
          };
          break;
        }
      }
      this.representativesCreateStore.clearFields();
    } else if (changes.added) {
      const values = this.representativesCreateStore.entityValues;
      if (!this.entityValues.showRepresentatives) {
        this.entityValues.showRepresentatives = [];
      }
      this.entityValues.showRepresentatives = [
        ...this.entityValues.showRepresentatives,
        {
          ...values,
          id: `${this.uniqueCounter++}`,
          __status__: "added",
        },
      ];
      this.representativesCreateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const representatives = this.entityValues.showRepresentatives;
      for (
        let index = 0;
        index < this.entityValues.showRepresentatives.length;
        index++
      ) {
        if (representatives[index].id === id) {
          if (isOriginalCrudStatus(representatives[index].__status__)) {
            representatives[index] = {
              ...representatives[index],
              __status__: deleteStatus(representatives[index].__status__),
            };
          } else {
            this.entityValues.showRepresentatives =
              this.entityValues.showRepresentatives.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.representatives);
    //   this.setCRUDRowsFromValues(newChanges, this.representativesCreateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.representatives = getNewCRUDRows(newChanges, this.entityValues.representatives, this.uniqueCounter++);
    // }
  }

  managementSocietiesLoadRow(row: any) {
    this.managementSocietiesCreateStore.loadValuesFromRow(row);
  }

  managementSocietiesRestore(id: string) {
    const managementSocieties =
      this.entityValues.showManagementSocieties.filter((x) => x.id === id)[0];
    if (managementSocieties) {
      managementSocieties.__status__ = restoredStatus(
        managementSocieties.__status__
      );
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  managementSocietiesValidateChanges(_changes: GridChangeSet) {
    const errors = this.managementSocietiesCreateStore.validateData(
      this.managementSocietiesCreateStore.entityValues
    );

    // Uniqueness validation for subentity
    const currentValues = this.managementSocietiesCreateStore.entityValues;
    const existingRows = this.entityValues.showManagementSocieties || [];
    const activeRows = existingRows.filter(
      (row: any) => !row.__status__?.includes("deleted")
    );

    // Uniqueness check: collectingSociety
    const isDuplicate_collectingSociety = activeRows.some(
      (row) =>
        row.id !== currentValues.id &&
        row.showCollectingSociety?.id ===
          currentValues.showCollectingSociety?.id &&
        currentValues.showCollectingSociety?.id
    );
    if (isDuplicate_collectingSociety) {
      errors.collectingSociety = {
        message: this.langStore.duplicateValueError,
        fieldLabel:
          this.langStore.PublisherCollectingSocietyCollectingSocietyLabel,
      };
    }

    this.managementSocietiesCreateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  managementSocietiesCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.managementSocietiesCreateStore.entityValues;
      const managementSocieties = this.entityValues.showManagementSocieties;
      for (
        let index = 0;
        index < this.entityValues.showManagementSocieties.length;
        index++
      ) {
        if (managementSocieties[index].id === values.id) {
          managementSocieties[index] = {
            ...values,
            __status__: updatedStatus(values.__status__),
          };
          break;
        }
      }
      this.managementSocietiesCreateStore.clearFields();
    } else if (changes.added) {
      const values = this.managementSocietiesCreateStore.entityValues;
      if (!this.entityValues.showManagementSocieties) {
        this.entityValues.showManagementSocieties = [];
      }
      this.entityValues.showManagementSocieties = [
        ...this.entityValues.showManagementSocieties,
        {
          ...values,
          id: `${this.uniqueCounter++}`,
          __status__: "added",
        },
      ];
      this.managementSocietiesCreateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const managementSocieties = this.entityValues.showManagementSocieties;
      for (
        let index = 0;
        index < this.entityValues.showManagementSocieties.length;
        index++
      ) {
        if (managementSocieties[index].id === id) {
          if (isOriginalCrudStatus(managementSocieties[index].__status__)) {
            managementSocieties[index] = {
              ...managementSocieties[index],
              __status__: deleteStatus(managementSocieties[index].__status__),
            };
          } else {
            this.entityValues.showManagementSocieties =
              this.entityValues.showManagementSocieties.filter(
                (x) => x.id !== id
              );
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.managementSocieties);
    //   this.setCRUDRowsFromValues(newChanges, this.managementSocietiesCreateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.managementSocieties = getNewCRUDRows(newChanges, this.entityValues.managementSocieties, this.uniqueCounter++);
    // }
  }

  getCreatedAt_Value(): Date | null {
    return this.entityValues.createdAt_;
  }

  setCreatedAt_Value(value: Date | null) {
    if (value !== null) {
      this.entityValues.createdAt_ = value;
    }
  }

  getLastUpdatedAt_Value(): Date | null {
    return this.entityValues.lastUpdatedAt_;
  }

  setLastUpdatedAt_Value(value: Date | null) {
    if (value !== null) {
      this.entityValues.lastUpdatedAt_ = value;
    }
  }

  addNewCountry(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Publisher",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/country/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewProvinceState(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Publisher",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/provinceState/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = PublisherValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      basicInformation: { open: true, errors: [] },
      address: { open: true, errors: [] },
      legalInformation: { open: true, errors: [] },
      systemDates: { open: false, errors: [] },
    };
    this.nationalitySearchValue = "";
    this.nationalityOptions = [];
    this.countrySearchValue = "";
    this.countryOptions = [];
    this.provinceSearchValue = "";
    this.provinceOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: PublisherValuesType): PublisherErrorsType {
    const errors: PublisherErrorsType = {};
    this.clearErrors();

    if (!values.personType) {
      errors.personType = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherPersonTypeLabel,
      };
      this.setCardError("basicInformation", errors.personType.message);
    }
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherNameLabel,
      };
      this.setCardError("basicInformation", errors.name.message);
    }
    if (!validateMask(values.taxId, { mask: "99-99999999-9" })) {
      errors.taxId = {
        message: this.langStore.maskedField("99-99999999-9"),
        fieldLabel: this.langStore.PublisherTaxIdLabel,
      };
    }
    values.taxId = values.taxId.trim();
    if (values.taxId === "") {
      errors.taxId = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherTaxIdLabel,
      };
      this.setCardError("basicInformation", errors.taxId.message);
    }
    values.phone = values.phone.trim();
    if (values.phone === "") {
      errors.phone = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherPhoneLabel,
      };
      this.setCardError("basicInformation", errors.phone.message);
    }

    if (values.email) {
      const normalizedEmail = normalizeAndValidateEmail(values.email);
      if (normalizedEmail === null) {
        errors.email = {
          message: this.langStore.badSyntaxEmail,
          fieldLabel: this.langStore.PublisherEmailLabel,
        };
        this.setCardError("basicInformation", errors.email.message);
      }
    } else {
      errors.email = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherEmailLabel,
      };
      this.setCardError("basicInformation", errors.email.message);
    }

    values.streetAndNumber = values.streetAndNumber.trim();
    if (values.streetAndNumber === "") {
      errors.streetAndNumber = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherStreetAndNumberLabel,
      };
      this.setCardError("address", errors.streetAndNumber.message);
    }
    values.postalCode = values.postalCode.trim();
    if (values.postalCode === "") {
      errors.postalCode = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherPostalCodeLabel,
      };
      this.setCardError("address", errors.postalCode.message);
    }
    this.countryError = undefined;
    if (values.showCountry === null || values.showCountry.id === "") {
      errors.country = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherCountryLabel,
      };
      this.setCardError("address", errors.country.message);
    }
    this.provinceError = undefined;
    if (values.showProvince === null || values.showProvince.id === "") {
      errors.province = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherProvinceLabel,
      };
      this.setCardError("address", errors.province.message);
    }
    values.city = values.city.trim();
    if (values.city === "") {
      errors.city = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherCityLabel,
      };
      this.setCardError("address", errors.city.message);
    }
    values.caeIpi = values.caeIpi.trim();
    if (values.caeIpi === "") {
      errors.caeIpi = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherCaeIpiLabel,
      };
      this.setCardError("legalInformation", errors.caeIpi.message);
    }
    if (values.showRepresentatives.length === 0) {
      errors.representatives = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.PublisherRepresentativesLabel,
      };
      this.setCardError("legalInformation", errors.representatives.message);
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "PublisherCreateStore"
    );
  }

  *duplicatesPublisher(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const publisherInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (publisherInstance) {
      this.entityValues = getPublisherValues(
        publisherInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la edición del registro creado.
      this.rootStore.router.replace(`/publisher/update/${returnData.resultId}`);
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewPublisher(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewPublisher(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      personType: this.entityValues.personType,
      name: this.entityValues.name,
      maritalStatus: this.entityValues.maritalStatus,
      pseudonym: this.entityValues.pseudonym,
      taxId: this.entityValues.taxId,
      phone: this.entityValues.phone,
      email: this.entityValues.email,
      streetAndNumber: this.entityValues.streetAndNumber,
      floor: this.entityValues.floor,
      suite: this.entityValues.suite,
      postalCode: this.entityValues.postalCode,
      city: this.entityValues.city,
      caeIpi: this.entityValues.caeIpi,
      createdAt_: this.entityValues.createdAt_,
      lastUpdatedAt_: this.entityValues.lastUpdatedAt_,
      nationality: this.entityValues.showNationality?.id || null,
      country: this.entityValues.showCountry?.id || null,
      province: this.entityValues.showProvince?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getPublisherValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = PublisherValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const variables = getPublisherCreateVariables(this.entityValues);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.publisherCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.publisherCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.publisherCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesPublisher(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.pseudonym = initialDisplayId;
      }

      yield this.loadNationalityValues();
      yield this.loadCountryValues();
      yield this.loadProvinceValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
